import { ToldLine } from '../../components/ToldLine'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  Badge,
  Button,
  Caption1,
  Checkbox,
  Field,
  Textarea,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
} from '@fluentui/react-components'
import {
  ArrowUndo16Regular,
  Checkmark16Regular,
  Dismiss16Regular,
} from '@fluentui/react-icons'
import { useUpdateApplicationStatus } from '../../hooks/useApplications'
import { useToast } from '../../components/ToastProvider'
import { activatable } from '../../lib/activatable'
import { apiErrorMessage } from '../../lib/apiError'
import { bayerColors, surfaces } from '../../theme'
import type { Demand } from '../../types/demand'
import type { Application, ApplicationStatus } from '../../types/application'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  headerCard: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontSize: '13px',
  },
  composition: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  compositionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
  },
  compositionLabel: {
    color: tokens.colorNeutralForeground3,
  },
  bulk: {
    background: surfaces.bulkPanelBg,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: surfaces.bulkPanelBorder,
    borderRightColor: surfaces.bulkPanelBorder,
    borderBottomColor: surfaces.bulkPanelBorder,
    borderLeftColor: surfaces.bulkPanelBorder,
    padding: tokens.spacingVerticalM,
  },
  shell: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 320px) 1fr',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
  },
  list: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    overflow: 'hidden',
  },
  listHead: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    background: tokens.colorNeutralBackground2,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: tokens.colorNeutralForeground3,
  },
  listItem: {
    padding: tokens.spacingVerticalS,
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    cursor: 'pointer',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    ':hover': { background: surfaces.proposalCardBg },
  },
  listItemActive: {
    background: bayerColors.navyLight,
  },
  itemName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  itemProposes: {
    fontSize: tokens.fontSizeBase100,
    color: bayerColors.navyMid,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: '2px',
  },
  itemMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  enrichRow: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
  },
  enrichSep: { color: tokens.colorNeutralForeground4 },
  detail: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  proposalCard: {
    background: surfaces.proposalCardBg,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  proposalHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: tokens.spacingVerticalS,
  },
  coverLetter: {
    fontSize: '13px',
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    whiteSpace: 'pre-wrap',
  },
  decisionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
})

export interface ApplicationsTabProps {
  demand: Demand
  applications: Application[]
}

const ROLE_LABEL: Record<string, string> = {
  talent: 'Talent',
  council: 'Council',
  admin: 'Admin',
  super_admin: 'Admin',
}

/**
 * Build the post-batch toast from the REAL outcome (not an optimistic count).
 * Exported for unit testing. Success → no intent (matches single-decide); a
 * partial failure → warning; a total failure → error.
 */
export function bulkResultToast(
  approved: number,
  failed: number,
  verb: string,
): { title: string; intent?: 'warning' | 'error' } {
  const s = (n: number) => (n === 1 ? '' : 's')
  if (failed === 0) return { title: `${approved} applicant${s(approved)} ${verb}` }
  if (approved === 0) return { title: `Batch failed — none ${verb} (${failed} error${s(failed)})`, intent: 'error' }
  return { title: `${approved} ${verb}, ${failed} failed`, intent: 'warning' }
}

/**
 * Demand detail Applications tab — mirrors mockup §7223-7456.
 *
 * Layout: header card with anticipated effort → composition summary
 * (approved / pending hours vs. target range) → bulk-decision panel (when
 * any rows are checked) → two-pane shell with applicant list + applicant
 * detail. Approve / Reject go through useUpdateApplicationStatus.
 */
export function ApplicationsTab({ demand, applications }: ApplicationsTabProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const toast = useToast()
  const updateStatus = useUpdateApplicationStatus()

  const [activeId, setActiveId] = useState<number | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [feedback, setFeedback] = useState('')

  // Feedback is a single field, but it is per-applicant in the single-review
  // pane. Clear it whenever the selected applicant changes so one applicant's
  // (required) feedback can never carry over and be submitted for — or posted
  // to the demand timeline against — the next applicant.
  useEffect(() => {
    setFeedback('')
  }, [activeId])

  const activeApp =
    applications.find((a) => a.id === activeId) ??
    applications.find((a) => a.status === 'pending') ??
    applications[0] ??
    null

  const composition = useMemo(() => {
    const approved = applications.filter((a) => a.status === 'approved')
    const pending = applications.filter((a) => a.status === 'pending')
    const approvedHours = approved.reduce(
      (s, a) => s + (a.proposed_hours_per_week ?? 0),
      0,
    )
    const pendingHours = pending.reduce(
      (s, a) => s + (a.proposed_hours_per_week ?? 0),
      0,
    )
    const target = demand.hours_per_week ?? null
    return {
      approved,
      pending,
      approvedHours,
      pendingHours,
      totalIfApproved: approvedHours + pendingHours,
      target,
    }
  }, [applications, demand.hours_per_week])

  // Batch approve/reject act on undecided applicants only. Selection is scoped
  // to the same set so the panel's count is the count that will change —
  // "Select all" used to tick every row, decided ones included, and then the
  // banner promised five decisions where one was possible.
  const undecided = applications.filter((a) => a.status === 'pending')

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (selected.size === undecided.length) setSelected(new Set())
    else setSelected(new Set(undecided.map((a) => a.id)))
  }

  function decide(id: number, status: ApplicationStatus) {
    if (status === 'pending') {
      // Retract — no feedback required
      updateStatus.mutate(
        { id, body: { status } },
        {
          onSuccess: () =>
            toast.showToast({ title: 'Decision retracted' }),
          onError: (err: unknown) => {
            toast.showToast({ title: apiErrorMessage(err, 'Retract failed'), intent: 'error' })
          },
        },
      )
      return
    }
    if (!feedback.trim()) {
      toast.showToast({ title: 'Feedback required', intent: 'warning' })
      return
    }
    updateStatus.mutate(
      { id, body: { status, feedback } },
      {
        onSuccess: () => {
          // Don't leave the just-submitted feedback in the shared field where
          // the next applicant would inherit it.
          setFeedback('')
          toast.showToast({ title: status === 'approved' ? 'Applicant approved' : 'Applicant rejected' })
        },
        onError: (err: unknown) => {
          toast.showToast({ title: apiErrorMessage(err, 'Decision failed'), intent: 'error' })
        },
      },
    )
  }

  async function bulkDecide(status: ApplicationStatus) {
    if (!feedback.trim()) {
      toast.showToast({ title: 'Feedback required', intent: 'warning' })
      return
    }
    const targets = applications.filter((a) => selected.has(a.id) && a.status === 'pending')
    if (targets.length === 0) return
    const verb = status === 'approved' ? 'approved' : 'rejected'
    // Await every decision and report the ACTUAL outcome — a failed row no
    // longer hides behind an optimistic count. Best-effort: one failure doesn't
    // block the rest. Keep only the failures selected so they can be retried.
    const results = await Promise.allSettled(
      targets.map((a) => updateStatus.mutateAsync({ id: a.id, body: { status, feedback } })),
    )
    const failedIds = targets.filter((_, i) => results[i]?.status === 'rejected').map((a) => a.id)
    toast.showToast(bulkResultToast(targets.length - failedIds.length, failedIds.length, verb))
    setSelected(new Set(failedIds))
  }

  if (applications.length === 0) {
    return (
      <MessageBar intent="info" data-testid="applications-empty">
        <MessageBarBody>No applications yet.</MessageBarBody>
      </MessageBar>
    )
  }

  const bulkMode = selected.size > 0

  // The demand has left the recruiting stage while people are still waiting on
  // an answer. Say so here rather than let the pane look like business as usual
  // — the applicants cannot see the stage, and nothing told them it moved.
  const undecidedCount = applications.filter((a) => a.status === 'pending').length
  const oldestPending = applications
    .filter((a) => a.status === 'pending')
    .sort((a, b) => a.created_at.localeCompare(b.created_at))[0]
  const strandedOutsideResourcing = demand.stage !== 'resourcing' && undecidedCount > 0

  return (
    <div className={styles.root} data-testid="applications-tab">
      {/* One bar about the waiting applicants, not two. It turns into a warning
          when the demand has left Resourcing, because then nobody told them. */}
      {oldestPending && (
        <MessageBar
          intent={strandedOutsideResourcing ? 'warning' : 'info'}
          data-testid={strandedOutsideResourcing ? 'applications-stranded' : undefined}
        >
          <MessageBarBody>
            {strandedOutsideResourcing ? (
              <>
                Left recruiting with {undecidedCount} undecided{' '}
                {undecidedCount === 1 ? 'applicant, who has' : 'applicants, who have'} not been told.
              </>
            ) : (
              <>
                {undecidedCount} awaiting a decision. Feedback goes to the applicant.
              </>
            )}{' '}
            Oldest submitted {new Date(oldestPending.created_at).toLocaleDateString()}.
          </MessageBarBody>
          <MessageBarActions>
            <Button onClick={() => setActiveId(oldestPending.id)}>Review oldest</Button>
          </MessageBarActions>
        </MessageBar>
      )}
      <div className={styles.headerCard}>
        <strong>{demand.demand_id || `DEM-${demand.id}`}</strong> · {demand.title}
        <div style={{ marginTop: 4, color: tokens.colorNeutralForeground3, fontSize: 12 }}>
          Anticipated effort:&nbsp;
          <strong>
            {composition.target ? `${composition.target} hrs/wk` : 'TBD'}
            {demand.duration ? ` · ${demand.duration}` : ''}
          </strong>{' '}
          · Each applicant proposes their own focus &amp; hours
        </div>
      </div>

      <CompositionSummary composition={composition} />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: bayerColors.navy }}>Applicants</h3>
        <Caption1>
          {applications.length} applicants ·{' '}
          {applications.filter((a) => a.status !== 'pending').length} decided
        </Caption1>
      </div>

      {bulkMode && (
        <div className={styles.bulk} data-testid="bulk-panel">
          <h4 style={{ margin: 0, fontSize: 14 }}>
            {selected.size} applicant{selected.size === 1 ? '' : 's'} selected for batch
            decision
          </h4>
          <Field
            label="Shared feedback (sent to all)"
            style={{ marginTop: tokens.spacingVerticalS, marginBottom: tokens.spacingVerticalS }}
          >
            <Textarea
              value={feedback}
              onChange={(_e, data) => setFeedback(data.value)}
              rows={2}
            />
          </Field>
          <div className={styles.decisionRow}>
            <Button
              appearance="primary"
              icon={<Checkmark16Regular />}
              disabled={!feedback.trim()}
              onClick={() => bulkDecide('approved')}
              data-testid="bulk-approve"
            >
              Approve all
            </Button>
            <Button
              appearance="secondary"
              icon={<Dismiss16Regular />}
              disabled={!feedback.trim()}
              onClick={() => bulkDecide('rejected')}
              data-testid="bulk-reject"
            >
              Reject all
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setSelected(new Set())}
              style={{ marginLeft: 'auto' }}
            >
              Clear selection
            </Button>
          </div>
        </div>
      )}

      <div className={styles.shell}>
        <div className={styles.list}>
          <div className={styles.listHead}>
            <Checkbox
              checked={selected.size === undecided.length && undecided.length > 0}
              onChange={toggleAll}
              disabled={undecided.length === 0}
              aria-label="Select all undecided applicants"
              data-testid="select-all"
            />
            <span>Applicants</span>
            {selected.size > 0 && (
              <span style={{ marginLeft: 'auto', color: bayerColors.navyMid }}>
                {selected.size} selected
              </span>
            )}
          </div>
          {applications.map((a) => {
            const isActive = activeApp?.id === a.id
            const role = a.applicant_role ? ROLE_LABEL[a.applicant_role] ?? a.applicant_role : null
            const dept = a.applicant_department ?? null
            const fa = a.applicant_focus_area_code ?? null
            const completed = a.applicant_completed_demands_count ?? 0
            return (
              <div
                key={a.id}
                className={`${styles.listItem}${isActive ? ` ${styles.listItemActive}` : ''}`}
                role="button"
                {...activatable(() => setActiveId(a.id))}
                data-testid={`applicant-row-${a.id}`}
              >
                <Checkbox
                  checked={selected.has(a.id)}
                  disabled={a.status !== 'pending'}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(a.id)}
                  aria-label={
                    a.status === 'pending'
                      ? `Select ${a.applicant_name ?? a.applicant_email ?? a.id}`
                      : `${a.applicant_name ?? a.applicant_email ?? a.id} is already decided`
                  }
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className={styles.itemName}
                    style={{ cursor: 'pointer', color: bayerColors.navyMid }}
                    role="link"
                    {...activatable(() => navigate(`/talents/${a.applicant_id}`), { nested: true })}
                  >
                    {a.applicant_name ?? a.applicant_email ?? `Applicant #${a.applicant_id}`}
                  </div>
                  <div className={styles.itemProposes}>
                    Proposes: {a.proposed_hours_per_week ?? '?'} hrs/wk ·{' '}
                    {a.proposed_duration ?? '?'}
                  </div>
                  <div className={styles.enrichRow} data-testid={`applicant-enrich-${a.id}`}>
                    {role && <span>{role}</span>}
                    {dept && (
                      <>
                        <span className={styles.enrichSep}>·</span>
                        <span>{dept}</span>
                      </>
                    )}
                    {fa && (
                      <>
                        <span className={styles.enrichSep}>·</span>
                        <span>{fa}</span>
                      </>
                    )}
                    <span className={styles.enrichSep}>·</span>
                    <span>
                      {completed} completed demand{completed === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className={styles.itemMeta}>
                    {new Date(a.created_at).toLocaleDateString()}
                    {a.status !== 'pending' && (
                      <Badge
                        appearance="tint"
                        color={
                          a.status === 'approved'
                            ? 'success'
                            : a.status === 'withdrawn'
                              ? 'informative'
                              : 'danger'
                        }
                        size="small"
                        style={{ marginLeft: 6 }}
                      >
                        {a.status === 'approved'
                          ? 'Contributor'
                          : a.status === 'withdrawn'
                            ? 'Withdrawn'
                            : 'Rejected'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {bulkMode ? null : activeApp ? (
          <ApplicantDetail
            applicant={activeApp}
            demand={demand}
            feedback={feedback}
            onFeedbackChange={setFeedback}
            onDecide={(status) => decide(activeApp.id, status)}
            styles={styles}
          />
        ) : (
          <div className={`${styles.detail} ${styles.empty}`}>
            Select an applicant to review.
          </div>
        )}
      </div>
    </div>
  )
}

interface CompositionProps {
  composition: {
    approved: Application[]
    approvedHours: number
    totalIfApproved: number
    target: number | null
  }
}

function CompositionSummary({ composition }: CompositionProps) {
  const styles = useStyles()
  const target = composition.target
  if (!target) return null

  const total = composition.totalIfApproved
  const status: 'in-range' | 'over' | 'under' =
    total >= target ? (total > target * 1.25 ? 'over' : 'in-range') : 'under'

  return (
    <div className={styles.composition} data-testid="composition-summary">
      <div className={styles.compositionRow}>
        <span className={styles.compositionLabel}>Approved so far</span>
        <span>
          <strong>{composition.approvedHours}</strong> hrs/wk ·{' '}
          {composition.approved.length} contributor
          {composition.approved.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className={styles.compositionRow}>
        <span className={styles.compositionLabel}>If all remaining approved</span>
        <span>
          <strong>{total}</strong> hrs/wk total
          <Badge
            appearance="tint"
            color={status === 'in-range' ? 'success' : status === 'over' ? 'warning' : 'informative'}
            size="small"
            style={{ marginLeft: 8 }}
          >
            {status === 'in-range'
              ? `within target (${target} hrs/wk)`
              : status === 'over'
              ? `over target by ${total - target} hrs/wk`
              : `under target by ${target - total} hrs/wk`}
          </Badge>
        </span>
      </div>
    </div>
  )
}

interface ApplicantDetailProps {
  applicant: Application
  demand: Demand
  feedback: string
  onFeedbackChange: (next: string) => void
  onDecide: (status: ApplicationStatus) => void
  styles: ReturnType<typeof useStyles>
}

function ApplicantDetail({
  applicant,
  demand: _demand,
  feedback,
  onFeedbackChange,
  onDecide,
  styles,
}: ApplicantDetailProps) {
  const navigate = useNavigate()
  const decided = applicant.status !== 'pending'
  const role = applicant.applicant_role
    ? ROLE_LABEL[applicant.applicant_role] ?? applicant.applicant_role
    : null
  const completed = applicant.applicant_completed_demands_count ?? 0

  return (
    <div className={styles.detail} data-testid={`applicant-detail-${applicant.id}`}>
      <div>
        <div
          style={{ fontWeight: 700, fontSize: 16, cursor: 'pointer', color: bayerColors.navyMid }}
          role="link"
          title="View talent profile"
          {...activatable(() => navigate(`/talents/${applicant.applicant_id}`))}
        >
          {applicant.applicant_name ?? applicant.applicant_email ?? `Applicant #${applicant.applicant_id}`}
        </div>
        <Caption1>
          {role && <>{role} · </>}
          {applicant.applicant_focus_area_code && (
            <>{applicant.applicant_focus_area_code} · </>
          )}
          {completed} completed demand{completed === 1 ? '' : 's'} · Applied{' '}
          {new Date(applicant.created_at).toLocaleDateString()}
        </Caption1>
      </div>

      {applicant.proposed_focus && (
        <div className={styles.proposalCard}>
          <div className={styles.proposalHead}>
            <strong style={{ fontSize: 12, textTransform: 'uppercase' }}>
              Their proposal
            </strong>
            <span style={{ fontSize: 13 }}>
              <strong>{applicant.proposed_hours_per_week ?? '?'} hrs/wk</strong> ·{' '}
              {applicant.proposed_duration ?? '?'}
            </span>
          </div>
          <div>{applicant.proposed_focus}</div>
        </div>
      )}

      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: tokens.colorNeutralForeground3,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            marginBottom: 4,
          }}
        >
          Cover letter
        </div>
        <div className={styles.coverLetter}>{applicant.cover_letter}</div>
      </div>

      {decided ? (
        <MessageBar intent={applicant.status === 'approved' ? 'success' : 'warning'}>
          <MessageBarBody>
            {applicant.status === 'approved' ? 'Approved' : 'Rejected'} — feedback
            recorded.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              size="small"
              appearance="subtle"
              icon={<ArrowUndo16Regular />}
              onClick={() => onDecide('pending')}
              data-testid={`retract-${applicant.id}`}
            >
              Retract
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : (
        <>
          <Field label="Feedback (required)" required>
            <Textarea
              rows={3}
              value={feedback}
              onChange={(_e, data) => onFeedbackChange(data.value)}
              placeholder="Be specific — feedback helps applicants understand your decision."
            />
          </Field>
          <ToldLine channel="email">the applicant, with your feedback</ToldLine>
          <div className={styles.decisionRow}>
            <Button
              appearance="primary"
              icon={<Checkmark16Regular />}
              disabled={!feedback.trim()}
              onClick={() => onDecide('approved')}
              data-testid={`approve-${applicant.id}`}
            >
              Approve
            </Button>
            <Button
              appearance="secondary"
              icon={<Dismiss16Regular />}
              disabled={!feedback.trim()}
              onClick={() => onDecide('rejected')}
              data-testid={`reject-${applicant.id}`}
            >
              Reject
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
